/**
 * BattleRoom State Manager - Simplified State Synchronization
 * 
 * @date_of_change: 2025-09-07
 * @purpose: Replace complex ref-based state sync with single source of truth pattern
 * @rule_reference: AGENTS.md - eliminate complex state synchronization anti-pattern
 * 
 * This utility provides a clean interface for managing BattleRoom state without the
 * complex ref-based synchronization that was causing race conditions and infinite loops.
 * It follows the principle of single source of truth from useBattleRoom hook.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { normalizeBattleLayerId } from './battleRoomLayerUtils';

const getInitialTargetingPopupPosition = () => {
  if (typeof window === 'undefined') return { x: 200, y: 150 };
  const compactViewport = window.innerWidth <= 900 || window.innerHeight <= 500;
  if (!compactViewport) return { x: 200, y: 150 };
  const popupWidth = window.innerWidth <= 600 ? 300 : 320;
  return {
    x: Math.max(8, window.innerWidth - popupWidth - 8),
    y: Math.min(72, Math.max(8, window.innerHeight - 180))
  };
};

/**
 * Layer State Management
 * Provides a clean interface for layer state without complex synchronization
 */
export const useBattleRoomLayers = (hookLayers, hookActiveLayer, setHookActiveLayer, setHookLayers) => {
  // Use hook state as the single source of truth
  // Local state only for UI enhancements that don't need persistence
  const [localUIState] = useState({
    layerIcons: {
      map: '🗺️',
      token: '👤', 
      gamemaster: '🎲'
    },
    layerDescriptions: {
      map: 'Static backgrounds and maps',
      token: 'Characters, monsters, and objects',
      gamemaster: 'GM-only assets and preparations'
    }
  });

  // Create enhanced layers by combining hook state with UI metadata
  const enhancedLayers = useMemo(() => ({
    map: {
      id: 'map',
      name: 'Map Layer',
      icon: localUIState.layerIcons.map,
      description: localUIState.layerDescriptions.map,
      visible: hookLayers?.map?.visible ?? true,
      locked: hookLayers?.map?.locked ?? false,
      ...hookLayers?.map // Merge any additional hook properties
    },
    token: {
      id: 'token',
      name: 'Token Layer', 
      icon: localUIState.layerIcons.token,
      description: localUIState.layerDescriptions.token,
      visible: hookLayers?.token?.visible ?? true,
      locked: hookLayers?.token?.locked ?? false,
      ...hookLayers?.token
    },
    gamemaster: {
      id: 'gamemaster',
      name: 'GameMaster Layer',
      icon: localUIState.layerIcons.gamemaster,
      description: localUIState.layerDescriptions.gamemaster,
      visible: hookLayers?.gamemaster?.visible ?? true,
      locked: hookLayers?.gamemaster?.locked ?? false,
      ...hookLayers?.gamemaster
    }
  }), [hookLayers, localUIState]);

  useEffect(() => {
    if (!hookLayers?.map?.mapImage || hookLayers.map.visible !== false) {
      return;
    }

    // A newly selected/restored background is also advertised in the minimap.
    // Keep the primary canvas in agreement because the compact layer selector
    // does not expose a visibility toggle. date_of_change: 2026-09-17
    setHookLayers((current) => {
      if (!current?.map?.mapImage || current.map.visible !== false) {
        return current;
      }

      return {
        ...current,
        map: {
          ...current.map,
          visible: true
        }
      };
    });
  }, [hookLayers?.map?.mapImage, hookLayers?.map?.visible, setHookLayers]);

  // Simple layer update function - directly updates hook state
  const updateLayerVisibility = useCallback((layerId, visible) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, layerId);
    console.log(`[BattleRoomStateManager] Updating layer ${normalizedLayerId} visibility:`, visible);
    setHookLayers(prev => ({
      ...prev,
      [normalizedLayerId]: {
        ...prev?.[normalizedLayerId],
        visible
      }
    }));
  }, [setHookLayers]);

  const updateLayerLocked = useCallback((layerId, locked) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, layerId);
    console.log(`[BattleRoomStateManager] Updating layer ${normalizedLayerId} locked:`, locked);
    setHookLayers(prev => ({
      ...prev,
      [normalizedLayerId]: {
        ...prev?.[normalizedLayerId],
        locked
      }
    }));
  }, [setHookLayers]);

  // Keep activation separate from manual lock preferences so toolbar switches do not silently
  // disable other layers or break token creation flows that target a different layer.
  const setActiveLayer = useCallback((layerId) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, 'map');
    console.log(`[BattleRoomStateManager] Setting active layer:`, normalizedLayerId);
    setHookActiveLayer(normalizedLayerId);
  }, [setHookActiveLayer]);

  // Asset draggability check - no complex state sync needed
  const getAssetDraggableInLayer = useCallback((tokenLayer) => {
    const layerToCheck = normalizeBattleLayerId(tokenLayer || 'token');
    const layerState = enhancedLayers[layerToCheck];
    const isLayerActive = layerToCheck === normalizeBattleLayerId(hookActiveLayer, 'map');
    const isLayerVisible = layerState?.visible !== false;
    const isLayerLocked = layerState?.locked === true;
    
    const isDraggable = isLayerActive && isLayerVisible && !isLayerLocked;
    
    console.log(`[BattleRoomStateManager] Asset draggability for '${layerToCheck}': ${isDraggable}`);
    return isDraggable;
  }, [enhancedLayers, hookActiveLayer]);

  return {
    layers: enhancedLayers,
    activeLayer: normalizeBattleLayerId(hookActiveLayer, 'map'),
    setActiveLayer,
    updateLayerVisibility,
    updateLayerLocked, 
    getAssetDraggableInLayer
  };
};

/**
 * UI State Management
 * Consolidates UI-only state that doesn't need persistence
 */
export const useBattleRoomUIState = () => {
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: null,
    assetType: null,
    assetId: null,
    assetLayer: null
  });

  const [tokenPropertiesPopup, setTokenPropertiesPopup] = useState({
    visible: false,
    tokenId: null,
    position: { x: 100, y: 100 },
    isPinned: false
  });

  const [multiattackTargetingPopup, setMultiattackTargetingPopup] = useState(() => ({
    visible: false,
    // Keep the first targeting tool clear of compact battle controls; the user
    // can then drag it anywhere and BattleRoom persists that position.
    // date_of_change: 2026-07-27
    position: getInitialTargetingPopupPosition(),
    isPinned: false
  }));

  const [selectedTokenIds, setSelectedTokenIds] = useState([]);
  const [draggedTokenId, setDraggedTokenId] = useState(null);
  const [targetingEnabled, setTargetingEnabled] = useState(false);

  // Marquee selection rectangle (stage-local coordinates)
  // @date_of_change: 2025-10-08 - Add selectionRect and isSelecting for multi-select drag
  // This state is UI-only and does not persist; used to render the selection box and
  // compute which tokens fall within it. Coordinates are in Stage local space, already
  // corrected for zoom/pan via getStageLocalPointer in BattleRoom.
  const [selectionRect, setSelectionRect] = useState(null); // { x, y, width, height } | null
  const [isSelecting, setIsSelecting] = useState(false);

  // Helper to close all popups
  const closeAllPopups = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
    setTokenPropertiesPopup(prev => ({ ...prev, visible: false }));
    setMultiattackTargetingPopup(prev => ({ ...prev, visible: false }));
  }, []);

  return {
    contextMenu,
    setContextMenu,
    tokenPropertiesPopup,
    setTokenPropertiesPopup,
    multiattackTargetingPopup,
    setMultiattackTargetingPopup,
    selectedTokenIds,
    setSelectedTokenIds,
    draggedTokenId,
    setDraggedTokenId,
    targetingEnabled,
    setTargetingEnabled,
    selectionRect,
    setSelectionRect,
    isSelecting,
    setIsSelecting,
    closeAllPopups
  };
};

/**
 * Debug helper for state validation
 */
export const validateStateConsistency = (layers, hookLayers, activeLayer, hookActiveLayer) => {
  const inconsistencies = [];
  const normalizedActiveLayer = normalizeBattleLayerId(activeLayer, activeLayer);
  const normalizedHookActiveLayer = normalizeBattleLayerId(hookActiveLayer, hookActiveLayer);
  
  // Check layer visibility consistency
  Object.keys(layers).forEach(layerId => {
    if (layers[layerId].visible !== hookLayers?.[layerId]?.visible) {
      inconsistencies.push(`Layer ${layerId} visibility mismatch`);
    }
    if (layers[layerId].locked !== hookLayers?.[layerId]?.locked) {
      inconsistencies.push(`Layer ${layerId} locked mismatch`);
    }
  });
  
  // Check active layer consistency  
  if (normalizedActiveLayer !== normalizedHookActiveLayer) {
    inconsistencies.push(`Active layer mismatch: local=${normalizedActiveLayer}, hook=${normalizedHookActiveLayer}`);
  }
  
  if (inconsistencies.length > 0) {
    console.warn('[BattleRoomStateManager] State inconsistencies detected:', inconsistencies);
  }
  
  return inconsistencies.length === 0;
};
